/**
 * ContactInbox — admin inbox for /contact submissions. Click a row → see the
 * full thread + reply box. Replies go to the visitor's email via SendGrid;
 * status flips from "new" → "replied" automatically.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Mail, MailOpen, Send, Archive, Loader2, ChevronRight, CheckCircle2 } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { adminApi } from "../lib/adminApi";

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return d;
  }
}

function statusBadge(status) {
  if (status === "new") return <Badge className="bg-[var(--csd-danger)] text-white">New</Badge>;
  if (status === "replied") return <Badge className="bg-emerald-600 text-white">Replied</Badge>;
  if (status === "archived") return <Badge variant="outline" className="text-[var(--csd-text-secondary)]">Archived</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

export default function ContactInbox({ messages, onChange }) {
  const [selectedId, setSelectedId] = useState(messages[0]?.id || null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);

  const selected = messages.find((m) => m.id === selectedId) || messages[0] || null;

  const handleReply = async () => {
    if (!selected || !reply.trim()) return;
    setSending(true);
    try {
      await adminApi.replyContactMessage(selected.id, reply.trim());
      toast.success("Reply sent — visitor received an email");
      setReply("");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reply");
    } finally {
      setSending(false);
    }
  };

  const handleStatus = async (status) => {
    if (!selected) return;
    setUpdating(true);
    try {
      await adminApi.setContactMessageStatus(selected.id, status);
      onChange?.();
    } catch (e) {
      toast.error("Could not update status");
    } finally {
      setUpdating(false);
    }
  };

  if (messages.length === 0) {
    return (
      <Card className="p-10 text-center" data-testid="messages-empty">
        <Mail className="mx-auto mb-3 text-[var(--csd-text-secondary)]" size={28} />
        <p className="text-sm text-[var(--csd-text-secondary)]">No messages yet.</p>
        <p className="text-xs text-[var(--csd-text-secondary)] mt-1">Visitors who submit /contact will appear here.</p>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4" data-testid="contact-inbox">
      {/* Inbox list */}
      <Card className="p-0 overflow-hidden max-h-[640px] overflow-y-auto">
        <div className="divide-y divide-[var(--csd-border)]" data-testid="messages-list">
          {messages.map((m) => {
            const isSelected = selected?.id === m.id;
            const isNew = m.status === "new";
            return (
              <button
                key={m.id}
                onClick={() => setSelectedId(m.id)}
                data-testid={`message-row-${m.id}`}
                className={`w-full text-left px-4 py-3 transition-colors ${
                  isSelected ? "bg-[var(--csd-primary-light)]/30" : "hover:bg-[var(--csd-bg)]/40"
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className={`text-sm font-semibold truncate ${isNew ? "text-gray-900" : "text-[var(--csd-text-secondary)]"}`}>
                    {m.name}
                  </span>
                  {isNew && <span className="w-2 h-2 rounded-full bg-[var(--csd-danger)] shrink-0 mt-1.5" />}
                </div>
                <div className="text-xs text-[var(--csd-text-secondary)] truncate mb-0.5">{m.email}</div>
                <div className={`text-xs truncate ${isNew ? "font-semibold text-gray-900" : "text-[var(--csd-text-secondary)]"}`}>
                  {m.subject || "(no subject)"}
                </div>
                <div className="text-[10px] text-[var(--csd-text-secondary)] mt-1 tabular-nums">{fmtDate(m.created_at)}</div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Detail + reply */}
      {selected ? (
        <Card className="p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-[var(--csd-border)] flex items-start gap-3">
            <MailOpen size={18} className="text-[var(--csd-primary)] mt-1" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-heading font-bold text-base text-gray-900 truncate" data-testid="message-subject">
                  {selected.subject || "(no subject)"}
                </h3>
                {statusBadge(selected.status)}
              </div>
              <div className="text-xs text-[var(--csd-text-secondary)]">
                <span className="font-semibold text-gray-900">{selected.name}</span>{" "}
                &lt;<a href={`mailto:${selected.email}`} className="text-[var(--csd-primary)] hover:underline">{selected.email}</a>&gt;
                {" · "}
                <span>{fmtDate(selected.created_at)}</span>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={updating || selected.status === "archived"}
              onClick={() => handleStatus("archived")}
              data-testid="message-archive"
              title="Archive"
            >
              <Archive size={14} className="mr-1" /> Archive
            </Button>
          </div>

          <div className="p-6 space-y-4 max-h-[640px] overflow-y-auto">
            {/* Original message */}
            <div data-testid="message-body" className="rounded-lg border border-[var(--csd-border)] bg-[var(--csd-bg)]/40 p-4">
              <div className="text-[10px] uppercase tracking-widest text-[var(--csd-text-secondary)] font-semibold mb-1.5">
                Original message
              </div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed text-gray-900">{selected.body}</p>
            </div>

            {/* Replies */}
            {(selected.replies || []).map((r, i) => (
              <div
                key={i}
                data-testid={`message-reply-${i}`}
                className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <CheckCircle2 size={14} className="text-emerald-600" />
                  <span className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">
                    Your reply · {fmtDate(r.sent_at)} · {r.email_status === "sent" ? "delivered" : r.email_status === "mocked" ? "logged (no SendGrid)" : "send error"}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap leading-relaxed text-gray-900">{r.body}</p>
              </div>
            ))}

            {/* Reply composer */}
            <div className="pt-4 border-t border-[var(--csd-border)]">
              <div className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1">
                <Send size={14} className="text-[var(--csd-primary)]" />
                Reply to {selected.name}
              </div>
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={5}
                placeholder={`Hi ${selected.name.split(" ")[0]},\n\nThanks for reaching out…`}
                maxLength={5000}
                data-testid="reply-body"
                className="resize-y"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-[var(--csd-text-secondary)] tabular-nums">{reply.length}/5000</span>
                <Button
                  type="button"
                  onClick={handleReply}
                  disabled={sending || !reply.trim()}
                  data-testid="reply-send"
                  className="h-10 rounded-xl bg-[var(--csd-primary)] hover:bg-[var(--csd-primary)]/90 text-white"
                >
                  {sending ? <><Loader2 size={14} className="mr-2 animate-spin" /> Sending…</> : <><Send size={14} className="mr-2" /> Send reply</>}
                </Button>
              </div>
              <p className="text-xs text-[var(--csd-text-secondary)] mt-2">
                Your reply is emailed to <strong>{selected.email}</strong> from the CarSaleDoc support address — they receive it like a normal email response. <ChevronRight size={10} className="inline" /> Goes via SendGrid.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-10 text-center text-[var(--csd-text-secondary)]">
          Select a message to read and reply.
        </Card>
      )}
    </div>
  );
}
